/**
 * Auth Layout Component
 *
 * Provides a consistent layout for authentication pages.
 */

import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Logger, LogCategory } from '@/utils/logger';
import { APP_NAME, ROUTES } from '@/constants';

// ============================================================================
// Types
// ============================================================================

interface AuthLayoutProps {
  children: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function AuthLayout({ children }: AuthLayoutProps) {
  Logger.debug(LogCategory.UI, 'Rendering AuthLayout', 'AuthLayout');

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1F497D] p-12 flex-col justify-between">
        <div>
          <Link to={ROUTES.HOME} className="flex items-center gap-3 text-white">
            <img
              src="/favicon.png"
              alt="RepuCall"
              className="h-10 w-10 rounded-lg"
            />
            <span className="text-xl font-bold">{APP_NAME}</span>
          </Link>
        </div>

        <div className="space-y-6">
          <h2 className="text-4xl font-bold text-white">
            Monitor and protect your phone number reputation
          </h2>
          <p className="text-lg text-blue-100">
            Track caller ID reputation across multiple sources, receive real-time
            alerts, and maintain clean phone number pools for your business communications.
          </p>

          {/* Feature highlights */}
          <div className="grid grid-cols-2 gap-4 mt-8">
            {[
              { title: '10+', desc: 'Reputation Sources' },
              { title: '24/7', desc: 'Monitoring' },
              { title: '99.9%', desc: 'Uptime' },
              { title: '<1s', desc: 'Response Time' },
            ].map((item, index) => (
              <div
                key={index}
                className="p-4 bg-white/10 rounded-lg backdrop-blur-sm"
              >
                <p className="text-2xl font-bold text-white">{item.title}</p>
                <p className="text-sm text-blue-200">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonial */}
        <div className="mt-auto pt-12">
          <blockquote className="text-blue-100 italic">
            "RepuCall has helped us reduce our flagged numbers by
            75% in just 3 months. Essential tool for any business that relies on
            outbound calling."
          </blockquote>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
              MR
            </div>
            <div>
              <p className="text-white font-medium">Marcus Reeves</p>
              <p className="text-sm text-blue-200">VP of Operations, Bridgepoint Communications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        {/* Mobile Logo */}
        <div className="lg:hidden absolute top-6 left-6">
          <Link to={ROUTES.HOME} className="flex items-center gap-2 text-slate-900">
            <img
              src="/favicon.png"
              alt="RepuCall"
              className="h-8 w-8 rounded-lg"
            />
            <span className="text-lg font-bold text-[#1F497D]">{APP_NAME}</span>
          </Link>
        </div>

        {children}
      </div>
    </div>
  );
}
